import { Row } from '@bridebook/ui';
import SearchSuppliersContainer from '@bridebook/web/app-basic/components/search-suppliers/search-suppliers-container';
import { TSearchSectionBlockData } from '@bridebook/web/app-shared/lib/search-suppliers/types';
import SearchSectionBlockItem from '../search-section-block-item';
import componentStyles from './search-section-block.style';
import { ReactNode, useMemo } from 'react';
import { A, Text, Flex, cn } from '@bridebook/aura';
import { useTranslation } from 'next-i18next';

interface SearchSectionBlockProps {
  section?: string;
  sectionClassName?: string;
  heading?: string;
  headingUrl?: string;
  data: TSearchSectionBlockData | null;
  slug: string;
  buildItem: (...args: any) => { title: string; href: string };
  sortByTitle?: boolean;
  carousel?: ReactNode;
  nativeRegionName?: string;
}

const SearchSectionBlock = ({
  section,
  sectionClassName,
  heading,
  data,
  slug,
  buildItem,
  headingUrl,
  nativeRegionName,
  sortByTitle = true,
  carousel = null,
}: SearchSectionBlockProps) => {
  const { t } = useTranslation('search');

  const dataList = useMemo(() => {
    const list = (data || []).map((item) => buildItem(slug, item));
    if (sortByTitle) {
      list.sort((a, b) => a.title.localeCompare(b.title));
    }
    return list;
  }, [data, buildItem, slug, sortByTitle]);

  const styles = useMemo(() => componentStyles({ itemsCount: dataList.length }), [dataList.length]);

  return (
    <SearchSuppliersContainer>
      <div className="pb-3.5 lg:pb-5" data-name="search-section-block">
        {section && (
          <Text
            as="h2"
            className={cn('mt-8 mb-6 text-2xl font-semibold lg:mt-20', sectionClassName)}>
            {section}
          </Text>
        )}
        {heading && headingUrl && (
          <Flex className="pt-6">
            <A className="mb-4 text-lg font-semibold text-space" href={headingUrl}>
              {t('searchSectionBlock.heading.top', { heading })}
            </A>
          </Flex>
        )}

        {carousel && (
          <>
            <div className="-mr-6 lg:mr-0">{carousel}</div>
            {nativeRegionName && headingUrl && (
              <A
                href={headingUrl}
                className="mt-0 mb-4 text-lg font-semibold text-indigo-crush sm:text-base">
                {nativeRegionName}
              </A>
            )}
          </>
        )}

        <Row style={styles.columnsOrientation}>
          {dataList.map((item) => (
            <SearchSectionBlockItem
              key={item.href + '_' + item.title}
              title={item.title}
              urlHref={item.href}
            />
          ))}
        </Row>
      </div>
    </SearchSuppliersContainer>
  );
};

export default SearchSectionBlock;
